import { Timestamp } from '#/components/timestamp';
import { Button } from '#/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/components/ui/tooltip';
import { EthereumStakingValidatorInfo } from '#/data/react-query/queries/order-list';
import { ValidatorStatus } from '#/modules/display/validator-status';
import { ArrowUpToLine, Ellipsis, History, LogOut, Zap } from 'lucide-react';
// import { ReactComponent as DepositIcon } from '../images/actions/deposit.svg';
import { CredentialType } from '#/components/ethereum/credential-type';
import { Dialog, DialogTrigger } from '#/components/ui/dialog';
import { HoverCard, HoverCardTrigger } from '#/components/ui/hover-card';
import { BeaconStatus } from '#/constants/pectra/beacon-status';
import { CredentialTypeEnum } from '#/constants/pectra/credential-type';
import { cn } from '#/lib/utils';
import { ConsolidateValidatorDialogContent } from '#/modules/dialogs/consolidate-validator-dialog';
import { ExitDialogContent } from '#/modules/dialogs/exit-dialog';
import { PartialWithdrawalDialogContent } from '#/modules/dialogs/partial-withdrawal-dialog';
import { UpgradeDialogContent } from '#/modules/dialogs/upgrade-dialog';
import { ValidatorEventHoverCardContent } from '#/modules/display/validator-event';
import { ValidatorIndex } from '#/modules/display/validator-index';
import { TopUpDialogContent } from '#/modules/top-up-dialog';
import { Translation } from 'intl-lib';
import { useMemo, useState } from 'react';
import { parseEther } from 'viem';
import { ReactComponent as ConsolidateIcon } from '../images/actions/consolidate.svg';
import { ReactComponent as PartialWithdrawalIcon } from '../images/actions/partial-withdrawal.svg';

type Props = {
  item: EthereumStakingValidatorInfo;
};

export function ValidatorRow({ item }: Props) {
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const [isPartialWithdrawalOpen, setIsPartialWithdrawalOpen] = useState(false);
  const [isConsolidateOpen, setIsConsolidateOpen] = useState(false);
  const [isExitOpen, setIsExitOpen] = useState(false);

  const isActiveOngoing = useMemo(
    () => item.beaconStatus === BeaconStatus.ACTIVE_ONGOING,
    [item]
  );
  const isConsolidatable = useMemo(
    () => item.credentialType == CredentialTypeEnum.TYPE2,
    [item]
  );
  const isPartialWithdrawalable = useMemo(
    () => parseEther(item.effectiveBalance) > parseEther('32'),
    [item]
  );
  const isLegacy = useMemo(
    () => item.credentialType === CredentialTypeEnum.TYPE1,
    [item]
  );
  const isFullCompounding = useMemo(
    () => item.credentialType === CredentialTypeEnum.FULL_COMPOUNDING,
    [item]
  );
  return (
    <div
      role="row"
      className="group/validator relative grid grid-cols-10 items-center gap-2 h-13"
    >
      <div role="cell" className="col-span-2 flex items-center">
        <ValidatorIndex
          validatorIndex={item.validatorIndex}
          publicKey={item.pubKey}
        />
      </div>
      <div role="cell" className="col-span-2 flex items-center gap-2">
        <CredentialType credentialType={item.credentialType} />
        {item.credentialType === CredentialTypeEnum.TYPE1 && (
          <Dialog>
            <DialogTrigger
              asChild
              onClick={event =>
                !(isActiveOngoing && isLegacy) && event.preventDefault()
              }
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn('rounded-full', {
                  'opacity-50': !(isActiveOngoing && isLegacy),
                })}
              >
                <Zap />
                <Translation path="my_staking.upgrade_dialog.trigger" />
              </Button>
            </DialogTrigger>
            <UpgradeDialogContent originalRow={item} />
          </Dialog>
        )}
      </div>
      <div role="cell" className="truncate">
        {item.effectiveBalance} ETH
      </div>
      <div role="cell" className="col-span-2 truncate">
        <Timestamp date={item.lastUpdate} />
      </div>
      <div role="cell" className="flex items-center gap-1">
        {item.event != null && item.event.length > 0 ? (
          <HoverCard openDelay={0}>
            <HoverCardTrigger className="cursor-pointer" asChild>
              <div className="relative">
                <ValidatorStatus item={item} />
                <div className="absolute right-0 top-0 p-px rounded-full bg-indigo-50 translate-x-1/4 -translate-y-1/4">
                  <History className="size-3 text-indigo-600" />
                </div>
              </div>
            </HoverCardTrigger>
            <ValidatorEventHoverCardContent item={item} />
          </HoverCard>
        ) : (
          <ValidatorStatus item={item} />
        )}
      </div>
      <div
        role="cell"
        className="col-span-2 flex justify-end items-center pr-2.5"
      >
        <Ellipsis className="size-4" />
      </div>
      <div
        role="toolbar"
        className={cn(
          'absolute top-0 right-0 bottom-0 bg-white transition',
          'flex items-center gap-2',
          'invisible opacity-0 group-hover/rowgroup:visible group-hover/rowgroup:opacity-100'
        )}
      >
        <Dialog>
          <Tooltip>
            <DialogTrigger asChild onClick={event => event.preventDefault()}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="rounded-full opacity-50 hidden" // fixme
                >
                  <ArrowUpToLine />
                </Button>
              </TooltipTrigger>
            </DialogTrigger>
            <TooltipContent>Top-up</TooltipContent>
          </Tooltip>
          <TopUpDialogContent originalRow={item} />
        </Dialog>
        <Dialog>
          <Tooltip>
            <DialogTrigger
              asChild
              onClick={event =>
                !(isActiveOngoing && isPartialWithdrawalable) &&
                event.preventDefault()
              }
            >
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn('rounded-full', {
                    'opacity-50': !(isActiveOngoing && isPartialWithdrawalable),
                  })}
                >
                  <PartialWithdrawalIcon />
                </Button>
              </TooltipTrigger>
            </DialogTrigger>
            <TooltipContent>Partial Withdrawal</TooltipContent>
          </Tooltip>
          <PartialWithdrawalDialogContent originalRow={item} />
        </Dialog>
        <Dialog>
          <Tooltip>
            <DialogTrigger
              asChild
              onClick={event =>
                !(isActiveOngoing && isConsolidatable) && event.preventDefault()
              }
            >
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn('rounded-full', {
                    'opacity-50': !(isActiveOngoing && isConsolidatable),
                  })}
                >
                  <ConsolidateIcon />
                </Button>
              </TooltipTrigger>
            </DialogTrigger>
            <TooltipContent>Consolidate into this validator</TooltipContent>
          </Tooltip>
          <ConsolidateValidatorDialogContent originalRow={item} />
        </Dialog>
        <Dialog>
          <Tooltip>
            <DialogTrigger
              asChild
              onClick={event => !isActiveOngoing && event.preventDefault()}
            >
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn('rounded-full', {
                    'opacity-50': !isActiveOngoing,
                  })}
                >
                  <LogOut />
                </Button>
              </TooltipTrigger>
            </DialogTrigger>
            <TooltipContent>Exit</TooltipContent>
          </Tooltip>
          <ExitDialogContent originalRow={item} />
        </Dialog>
      </div>
    </div>
  );
}
